import { Anchor, Button, Table } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { stevilo } from '../oblika/oblika';
import { GlavaZaslona, IskalnoPolje, Seznam, Stolpec, ZaslonSeznama } from '../seznam/Seznam';
import { useSeznam } from '../seznam/useSeznam';

type Razvrsti = 'registrska' | 'vozilo' | 'letnik' | 'km' | 'kupec';

/**
 * Vozila strank (modul Delavnica): iskanje po registrski ali VIN (presledki in vezaji niso pomembni), vozilu in
 * lastniku, razvrščanje in strani na strežniku. Klik odpre vozilo s servisno knjigo.
 */
export function PregledVozilStrank() {
  const seznam = useSeznam<Razvrsti>();
  const navigate = useNavigate();
  const poizvedba = useQuery({
    queryKey: ['vozila-strank', seznam.iskanje, seznam.zaApi],
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/delavnica/vozila/pregled', {
          signal,
          params: { query: { iskanje: seznam.iskanje || undefined, ...seznam.zaApi } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const vrstice = poizvedba.data?.vsebina ?? [];

  return (
    <ZaslonSeznama>
      <GlavaZaslona
        naslov="Vozila strank"
        opis="Vozila, ki so bila v delavnici, s servisno knjigo."
        pomoc="vozila-strank"
        dejanja={
          <Button component={Link} to="/delavnica/vozila/novo" leftSection={<IconPlus size={18} />}>
            Novo vozilo
          </Button>
        }
      />
      <Seznam
        seznam={seznam}
        filtri={<IskalnoPolje seznam={seznam} namig="Išči po registrski, VIN, znamki, modelu ali lastniku" />}
        glava={
          <>
            <Stolpec seznam={seznam} kljuc="registrska" naziv="Registrska" />
            <Stolpec seznam={seznam} kljuc="vozilo" naziv="Vozilo" />
            <Stolpec seznam={seznam} kljuc="letnik" naziv="Letnik" />
            <Stolpec seznam={seznam} kljuc="km" naziv="Km" desno />
            <Stolpec seznam={seznam} kljuc="kupec" naziv="Lastnik" />
          </>
        }
        vrstice={vrstice.map((v, i) => (
          <Table.Tr key={v.id} style={{ cursor: 'pointer' }} onClick={() => void navigate(`/delavnica/vozila/${v.id}`)}>
            <Table.Td c="dimmed" className="brez-preloma">
              {seznam.zamik + i + 1}
            </Table.Td>
            <Table.Td>
              <Anchor
                component={Link}
                to={`/delavnica/vozila/${v.id}`}
                onClick={(e) => e.stopPropagation()}
                fw={600}
                className="brez-preloma"
              >
                {v.registrska || v.vin}
              </Anchor>
            </Table.Td>
            <Table.Td>{[v.znamka, v.model].filter(Boolean).join(' ')}</Table.Td>
            <Table.Td>{v.letnik}</Table.Td>
            <Table.Td ta="right" className="brez-preloma">
              {v.km != null ? stevilo(v.km) : ''}
            </Table.Td>
            <Table.Td>{v.kupec}</Table.Td>
          </Table.Tr>
        ))}
        stVrstic={vrstice.length}
        stStolpcev={5}
        skupaj={poizvedba.data?.skupaj ?? 0}
        nalaganje={poizvedba.isPending}
        osvezevanje={poizvedba.isFetching}
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        prazno="Ni vozil za izbrane pogoje."
        napakaNaslov="Vozil ni bilo mogoče naložiti"
      />
    </ZaslonSeznama>
  );
}
